import React, { useState, useEffect } from 'react';
import { Plus, Landmark, PiggyBank, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import Breadcrumb from '../../components/Common/Breadcrumb';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { accountsService } from '../../services/accountsService';
import { toast } from 'react-toastify';
import '../../components/ConfirmationModal/ConfirmationModal.css';

const CashAccountMaster = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'cash',
    currency: 'INR',
    openingBalance: 0,
    openingDate: new Date().toISOString().split('T')[0],
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await accountsService.getCashAccounts();
      if (response?.success) {
        const transformed = (response.data || []).map(item => ({
          ...item,
          id: item._id
        }));
        setData(transformed);
      } else {
        toast.error('Failed to load cash accounts');
      }
    } catch (error) {
      console.error('Error loading cash accounts:', error);
      toast.error('Failed to load cash accounts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'cash',
      currency: 'INR',
      openingBalance: 0,
      openingDate: new Date().toISOString().split('T')[0],
      isActive: true
    });
    setEditingItem(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      type: item.type || 'cash',
      currency: item.currency || 'INR',
      openingBalance: item.openingBalance ?? 0,
      openingDate: item.openingDate
        ? new Date(item.openingDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      isActive: item.isActive !== false
    });
    setShowModal(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Account name is required');
      return;
    }
    try {
      const payload = {
        ...formData,
        openingBalance: Number(formData.openingBalance) || 0
      };
      if (editingItem) {
        const response = await accountsService.updateCashAccount(editingItem._id, payload);
        if (response?.success) {
          toast.success('Cash account updated successfully');
          setShowModal(false);
          loadData();
        } else {
          toast.error(response?.message || 'Failed to update');
        }
      } else {
        const response = await accountsService.createCashAccount(payload);
        if (response?.success) {
          toast.success('Cash account created successfully');
          setShowModal(false);
          loadData();
        } else {
          toast.error(response?.message || 'Failed to create');
        }
      }
    } catch (error) {
      console.error('Error saving cash account:', error);
      toast.error(error?.response?.data?.message || 'Failed to save');
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const response = await accountsService.deleteCashAccount(deleteItem._id);
      if (response?.success) {
        toast.success('Cash account deleted');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      } else {
        toast.error(response?.message || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete');
    }
  };

  const formatCurrency = (amount, currency = 'INR') =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount || 0);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue, row }) => (
        <div className="d-flex align-items-center gap-2">
          {row.original.type === 'bank' ? (
            <Landmark size={16} className="text-primary" />
          ) : (
            <PiggyBank size={16} className="text-success" />
          )}
          <span className="fw-medium">{getValue()}</span>
        </div>
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() === 'bank' ? 'bg-primary' : 'bg-success'}`}>
          {getValue()}
        </span>
      )
    },
    {
      accessorKey: 'currency',
      header: 'Currency'
    },
    {
      accessorKey: 'openingBalance',
      header: 'Opening Balance',
      cell: ({ getValue, row }) => formatCurrency(getValue(), row.original.currency)
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() ? 'bg-success' : 'bg-secondary'}`}>
          {getValue() ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const breadcrumbItems = [
    { label: 'Accounts', path: '/accounts', onClick: () => navigate('/accounts') },
    { label: 'Masters', path: '/accounts?tab=masters', onClick: () => navigate('/accounts?tab=masters') },
    { label: 'Cash Account Master', path: '/accounts/cash-accounts', isCurrent: true }
  ];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Cash Account Master</h1>
          <p>Manage cash and bank accounts for tracking</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Cash Account
          </button>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          noCard={true}
          className="cash-account-master-table"
          pageSize={10}
        />
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="confirmation-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && (setShowModal(false), resetForm())}
        >
          <div className="confirmation-modal" style={{ maxWidth: '480px' }}>
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">
                {editingItem ? 'Edit Cash Account' : 'Add Cash Account'}
              </h3>
              <button
                className="confirmation-modal-close"
                onClick={() => (setShowModal(false), resetForm())}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="confirmation-modal-content" style={{ padding: '1.5rem' }}>
              <div className="mb-3">
                <label className="form-label">Account Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Petty Cash, HDFC Current"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => setFormData(f => ({ ...f, type: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Currency</label>
                <select
                  className="form-select"
                  value={formData.currency}
                  onChange={(e) => setFormData(f => ({ ...f, currency: e.target.value }))}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Opening Balance</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={formData.openingBalance}
                  onChange={(e) => setFormData(f => ({ ...f, openingBalance: e.target.value }))}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Opening Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.openingDate}
                  onChange={(e) => setFormData(f => ({ ...f, openingDate: e.target.value }))}
                />
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(f => ({ ...f, isActive: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="isActive">Active</label>
              </div>
            </div>
            <div className="confirmation-modal-footer" style={{ padding: '1rem 1.5rem' }}>
              <button className="confirmation-modal-cancel" onClick={() => (setShowModal(false), resetForm())}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {editingItem ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => (setShowDeleteModal(false), setDeleteItem(null))}
        onConfirm={confirmDelete}
        title="Delete Cash Account"
        message={`Are you sure you want to delete "${deleteItem?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default CashAccountMaster;
